import { whatsappColors, type ConversationItemData } from './types';
import { ConversationListItem } from './ConversationListItem';

interface ConversationListProps {
  conversations: ConversationItemData[];
  onSelectConversation: (id: string) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  filterStatus: 'all' | 'open' | 'closed';
  onFilterChange: (status: 'all' | 'open' | 'closed') => void;
  isLoading?: boolean;
  error?: string;
  onRetry?: () => void;
}

export function ConversationList({
  conversations,
  onSelectConversation,
  searchValue,
  onSearchChange,
  filterStatus,
  onFilterChange,
  isLoading,
  error,
  onRetry,
}: ConversationListProps) {
  return (
    <div className="h-full flex flex-col w-full"
      style={{ backgroundColor: whatsappColors.panelBg }}
    >
      <style>{`
        .whatsapp-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .whatsapp-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .whatsapp-scrollbar::-webkit-scrollbar-thumb {
          background-color: rgba(11, 20, 26, 0.1);
          border-radius: 3px;
        }
        .whatsapp-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: rgba(11, 20, 26, 0.16);
        }
      `}</style>

      {/* Header */}
      <div 
        className="shrink-0 px-4 py-2.5 flex items-center justify-between"
        style={{ backgroundColor: whatsappColors.headerBg, borderBottom: `1px solid ${whatsappColors.borderDark}` }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div 
            className="w-10 h-10 rounded-full bg-[#dfe5e7] flex items-center justify-center text-base font-medium"
            style={{ color: whatsappColors.textPrimary }}
          >
            D
          </div>
          <div className="min-w-0">
            <div 
              className="text-base font-medium truncate"
              style={{ color: whatsappColors.textPrimary }}
            >
              Dex Development
            </div>
            <div 
              className="text-xs truncate"
              style={{ color: whatsappColors.textSecondary }}
            >
              {conversations.length} conversas
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0" style={{ color: whatsappColors.iconGray }}>
          <button 
            className="p-2 rounded-full transition-colors hover:bg-[#f5f6f6]"
            title="Nova conversa"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.005 3.175H4.674C3.642 3.175 3 3.789 3 4.821V21.02l3.544-3.514h12.461c1.033 0 2.064-1.06 2.064-2.093V4.821c-.001-1.032-1.032-1.646-2.064-1.646zm-4.989 9.869H7.041V11.1h6.975v1.944zm3-4H7.041V7.1h9.975v1.944z"/>
            </svg>
          </button>
          <button 
            className="p-2 rounded-full transition-colors hover:bg-[#f5f6f6]"
            title="Menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 7a2 2 0 1 0-.001-4.001A2 2 0 0 0 12 7zm0 2a2 2 0 1 0-.001 3.999A2 2 0 0 0 12 9zm0 6a2 2 0 1 0-.001 3.999A2 2 0 0 0 12 15z"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Search */}
      <div 
        className="shrink-0 px-3 pt-2 pb-3"
        style={{ backgroundColor: whatsappColors.panelBg, borderBottom: `1px solid ${whatsappColors.border}` }}
      >
        <div 
          className="flex items-center gap-3 rounded-lg px-4 py-2"
          style={{ backgroundColor: whatsappColors.searchBg }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={whatsappColors.iconGray} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            className="w-full bg-transparent outline-none text-[15px]"
            style={{ color: whatsappColors.textPrimary }}
            placeholder="Pesquisar ou começar uma nova conversa"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        <div className="mt-2.5 flex items-center gap-2">
          <button
            onClick={() => onFilterChange('all')}
            className="text-[12.5px] font-medium px-3 py-1.5 rounded-full border transition-all"
            style={{
              backgroundColor: filterStatus === 'all' ? whatsappColors.greenLight : whatsappColors.panelBg,
              borderColor: filterStatus === 'all' ? '#a7efbf' : whatsappColors.border,
              color: filterStatus === 'all' ? whatsappColors.greenDark : whatsappColors.textSecondary,
            }}
          >
            Todas
          </button>
          <button
            onClick={() => onFilterChange('open')}
            className="text-[12.5px] font-medium px-3 py-1.5 rounded-full border transition-all"
            style={{
              backgroundColor: filterStatus === 'open' ? whatsappColors.greenLight : whatsappColors.panelBg,
              borderColor: filterStatus === 'open' ? '#a7efbf' : whatsappColors.border,
              color: filterStatus === 'open' ? whatsappColors.greenDark : whatsappColors.textSecondary,
            }}
          >
            Abertas
          </button>
          <button
            onClick={() => onFilterChange('closed')}
            className="text-[12.5px] font-medium px-3 py-1.5 rounded-full border transition-all"
            style={{
              backgroundColor: filterStatus === 'closed' ? '#fee2e2' : whatsappColors.panelBg,
              borderColor: filterStatus === 'closed' ? '#fecaca' : whatsappColors.border,
              color: filterStatus === 'closed' ? '#991b1b' : whatsappColors.textSecondary,
            }}
          >
            Fechadas
          </button>
        </div>
      </div>

      {/* List */}
      <div 
        className="flex-1 overflow-y-auto whatsapp-scrollbar"
        style={{ backgroundColor: whatsappColors.panelBg }}
      >
        {isLoading ? (
          <div className="p-4 text-center" style={{ color: whatsappColors.textSecondary }}>
            Carregando conversas...
          </div>
        ) : error ? (
          <div className="p-4 text-center">
            <div style={{ color: whatsappColors.textSecondary }}>Falha ao carregar conversas</div>
            {onRetry && (
              <button 
                onClick={onRetry}
                className="mt-2 px-4 py-2 rounded-lg"
                style={{ backgroundColor: whatsappColors.green, color: 'white' }}
              >
                Tentar novamente
              </button>
            )}
          </div>
        ) : conversations.length === 0 ? (
          <div className="p-6 text-center" style={{ color: whatsappColors.textSecondary }}>
            Nenhuma conversa encontrada
          </div>
        ) : (
          <div style={{ borderTop: `1px solid ${whatsappColors.border}` }}>
            {conversations.map((conv) => (
              <ConversationListItem
                key={conv.id}
                data={conv}
                onClick={() => onSelectConversation(conv.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
